import { useState, useEffect } from 'react';
import { FileUploadError, FileUploadStatus } from '../data/types';
import { Text } from 'foundations-components/components/Text';
import styled from 'styled-components';
import { Image } from 'foundations-components/transitional/components/Image';
import { Flex } from 'foundations-components/components/Flex';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { XCircleIcon } from 'foundations-assets/icons/components/XCircleIcon';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import I18n from 'I18n';
import { View } from 'foundations-components/components/View';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getFileTypeIcon } from '../constants/attachments';
import FormattedSize from 'I18n/components/FormattedSize';
import { fwcBorderRadius } from 'foundations-theming/utils/fwc';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SmallSpinner = styled.div.withConfig({
  displayName: "FileUploadDisplay__SmallSpinner"
})(["display:flex;align-items:center;justify-content:center;width:18px;height:18px;& > *{transform:scale(0.75);}"]);
const UploadErrorMessage = ({
  error
}) => {
  switch (error) {
    case FileUploadError.SIZE_OVERFLOW_ERROR:
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customer-portal-app.attachErrors.tooLarge"
      });
    case FileUploadError.FILE_TYPE_NOT_SUPPORTED:
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customer-portal-app.attachErrors.unsupportedFileType"
      });
    case FileUploadError.RATE_OVERFLOW_ERROR:
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customer-portal-app.attachErrors.rateOverflowError"
      });
    case FileUploadError.UPLOAD_FAILED:
    case FileUploadError.UPLOAD_ABORTED:
    default:
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customer-portal-app.attachErrors.uploadError"
      });
  }
};
export function FileUploadDisplay({
  fileUpload,
  handleRemove
}) {
  const {
    previewUrl,
    file,
    status,
    error
  } = fileUpload;
  const {
    name,
    type
  } = file;
  const [imageLoadError, setImageLoadError] = useState(false);
  const failedUpload = status === FileUploadStatus.ERROR;
  const attachmentLoading = status === 'uploading' || status === 'pending';
  const FileIcon = getFileTypeIcon({
    mimeType: type,
    fileName: name
  });
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setImageLoadError(false);
  }, [previewUrl]);
  const shouldShowPreview = previewUrl && !imageLoadError;
  return /*#__PURE__*/_jsxs(Flex, {
    borderWidth: "100",
    borderStyle: "solid",
    borderColor: failedUpload ? 'border-alert-default' : 'border-core-subtle',
    borderRadius: fwcBorderRadius('fwc-alert'),
    backgroundColor: failedUpload ? 'fill-alert-subtle' : 'fill-surface-default',
    padding: "200",
    alignItems: "center",
    justifyContent: "start",
    flexDirection: "row",
    inlineSize: "240px",
    minInlineSize: "240px",
    blockSize: "58px",
    gap: "400",
    children: [shouldShowPreview ? /*#__PURE__*/_jsx(Image, {
      src: previewUrl,
      alt: name,
      height: "40px",
      width: "40px",
      borderRadius: "300",
      onError: () => setImageLoadError(true)
    }) : /*#__PURE__*/_jsx(Flex, {
      inlineSize: "40px",
      blockSize: "40px",
      backgroundColor: failedUpload ? 'fill-accent-red-subtle' : 'fill-surface-recessed',
      borderRadius: "300",
      alignItems: "center",
      justifyContent: "center",
      children: /*#__PURE__*/_jsx(FileIcon, {
        size: "300"
      })
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "100",
      flex: "1",
      minInlineSize: "0",
      children: [/*#__PURE__*/_jsx(Tooltip, {
        title: name,
        children: /*#__PURE__*/_jsx(View, {
          cursor: "pointer",
          children: /*#__PURE__*/_jsx(Text, {
            typography: "body-100",
            color: failedUpload ? 'text-alert-default' : 'text-core-default',
            truncate: true,
            children: name
          })
        })
      }), /*#__PURE__*/_jsx(Text, {
        typography: failedUpload ? 'heading-25' : 'detail-100',
        color: error ? 'text-alert-default' : 'text-core-default',
        children: failedUpload ? /*#__PURE__*/_jsx(UploadErrorMessage, {
          error: error
        }) : /*#__PURE__*/_jsx(FormattedSize, {
          value: file.size,
          precision: 1
        })
      })]
    }), attachmentLoading ? /*#__PURE__*/_jsx(SmallSpinner, {
      children: /*#__PURE__*/_jsx(LoadingSpinner, {
        size: "extra-small",
        "aria-label": I18n.text('customer-portal-app.loading')
      })
    }) : /*#__PURE__*/_jsx(IconButton, {
      onClick: handleRemove,
      tooltip: I18n.text('customer-portal-app.composerToolbar.removeAttachment'),
      "aria-label": I18n.text('customer-portal-app.composerToolbar.removeAttachment'),
      use: "unstyled",
      children: /*#__PURE__*/_jsx(XCircleIcon, {
        size: "300",
        color: "icon-core-subtle"
      })
    })]
  });
}